import React, { useEffect, useRef, useState, MouseEvent, JSX } from 'react';
import { Button, Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import {
  ApiAirportType,
  ApiFlightSearchGeoUnitsRequestFromJSON,
  ApiSearchGeoUnitsRequestFromJSON,
  ApiGeoUnit,
  ApiItemType,
  ApiFlightSearchGeoUnitsRequest,
  ApiSearchGeoUnitsRequest
} from '@ibe/api';
import { LoggerFactory } from '@ibe/services';
import fallback from '../../../../_Translations/generated/geo-unit-checkbox-async.en.t.json';
import Keys from '../../../../_Translations/generated/geo-unit-checkbox-async.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import Spinner from '../../UI/LoadingOverlay/Spinner';
import useApi from '../../../Hooks/useApi';

const logger = LoggerFactory.get('GeoUnitCheckboxAsync.tsx');
import { PSEUDO_CODE, TypeCodeKey, usePseudoGeoOption } from './externals';
import { useClickAway } from 'react-use';
import CustomInput from '../../CustomInput/CustomInput';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import useAppService from '../../../Hooks/useAppService';

const MAX_NUMBER_PER_COLUMN = 6;

export interface Props {
  itemType: ApiItemType;
  airportType?: ApiAirportType;
  value: ApiGeoUnit[] | undefined;
  placeholder?: string;
  onChange: (value: ApiGeoUnit[]) => void;
  name?: string;
  externalGeoUnits?: ApiGeoUnit[];
  changeOnBlur?: boolean;
  withSelectAll?: boolean;
  showPseudo?: boolean;
  withRemoveAll?: boolean;
  withApplyButton?: boolean;
  staticLabel?: string;
  possibleGeoUnitCodes?: string[];
  preSelectedOrigins?: string[];
  disabled?: boolean;
  valueToShow?: string;
  changeNumberOfColumnDynamically?: boolean;
  withDismissAllIcon?: boolean;
  selectAllOriginsOnInitialLoad?: boolean;
  showAllGeoUnitsInitially?: boolean;
  useTwoColumnStyling?: boolean;
  triggerSearchOnLeave?: boolean;
}

const GeoUnitCheckboxAsync = (props: Props): JSX.Element => {
  const {
    airportType,
    itemType,
    value = [],
    placeholder,
    name,
    onChange,
    externalGeoUnits,
    changeOnBlur,
    withSelectAll,
    showPseudo,
    withRemoveAll,
    staticLabel,
    possibleGeoUnitCodes,
    disabled,
    valueToShow,
    withApplyButton = true,
    changeNumberOfColumnDynamically,
    withDismissAllIcon,
    preSelectedOrigins,
    selectAllOriginsOnInitialLoad,
    showAllGeoUnitsInitially = true,
    useTwoColumnStyling,
    triggerSearchOnLeave = true
  } = props;
  const { t } = useTranslation('geo-unit-checkbox-async', fallback, Keys);
  const api = useApi();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<ApiGeoUnit[]>(externalGeoUnits || []);
  const [optionsForTwoColumnsDesktop, setOptionsForTwoColumnsDesktop] = useState<ApiGeoUnit[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ApiGeoUnit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const initialSelectionDone = useRef(false);
  const appService = useAppService();

  useEffect(() => {
    if (externalGeoUnits) {
      setOptions(externalGeoUnits);
    }
    if (externalGeoUnits && useTwoColumnStyling) {
      setOptionsForTwoColumnsDesktop(getMultiColGeoUnits(externalGeoUnits));
    }
  }, []);

  const getMultiColGeoUnits = (geoUnits: ApiGeoUnit[]) => {
    const firstList: ApiGeoUnit[] = [];
    const secondList: ApiGeoUnit[] = [];
    geoUnits?.forEach((foundOption: ApiGeoUnit, idx: number) => {
      const condition = withSelectAll ? idx % 2 === 0 : idx % 2 !== 0;
      const targetList = condition ? secondList : firstList;
      targetList.push(foundOption);
    });
    return [...firstList, ...secondList];
  };

  useEffect(() => {
    const load = async () => {
      if (preSelectedOrigins && preSelectedOrigins.length) {
        const foundOptions = await loadOptions();
        const preSelectedOriginsGeoUnits = (foundOptions || []).filter(opt =>
          preSelectedOrigins.includes(opt.id)
        );
        setSelectedOptions(preSelectedOriginsGeoUnits);
        onChange(preSelectedOriginsGeoUnits);
      } else if (selectAllOriginsOnInitialLoad) {
        const foundOptions = await loadOptions();
        if (foundOptions) {
          setSelectedOptions(foundOptions);
          onChange(foundOptions);
        }
      }
    };
    if (possibleGeoUnitCodes?.length && !initialSelectionDone.current) {
      load();
      initialSelectionDone.current = true;
    }
  }, [possibleGeoUnitCodes]);

  useEffect(() => {
    setSelectedOptions(value ? [...value] : []);
  }, [value]);

  const loadOptions = async (): Promise<ApiGeoUnit[] | undefined> => {
    let foundOptions: ApiGeoUnit[] | undefined = undefined;
    setIsLoading(true);
    const request = airportType
      ? ApiFlightSearchGeoUnitsRequestFromJSON({
          type: 'FlightSearchGeoUnitsRequest',
          search: '',
          offset: 0,
          limit: 200,
          itemType,
          airportType,
          languageCode: appService.lang
        } as ApiFlightSearchGeoUnitsRequest)
      : ApiSearchGeoUnitsRequestFromJSON({
          type: 'SearchGeoUnitsRequest',
          search: '',
          offset: 0,
          limit: 200,
          itemType,
          languageCode: appService.lang
        } as ApiSearchGeoUnitsRequest);
    try {
      const result = await api.searchGeoUnits(request);
      foundOptions =
        !!possibleGeoUnitCodes && possibleGeoUnitCodes.length > 0
          ? result.geoUnits.reduce((total: ApiGeoUnit[], current: ApiGeoUnit) => {
              let currentTotal = [...total];
              if (possibleGeoUnitCodes.includes(current.code)) {
                currentTotal = [...total, current];
              }
              return [
                ...currentTotal,
                ...Object.entries(current.children)
                  .filter(([, unit]) => possibleGeoUnitCodes.includes(unit.code))
                  .map(([, unit]) => unit)
              ];
            }, [])
          : showAllGeoUnitsInitially
          ? result.geoUnits
          : [];

      /* With css masonry (status: experimental as of now) two columns without vertical gaps
         will be easy (css only solution). For now it has to be this shite... */
      if (useTwoColumnStyling) {
        setOptionsForTwoColumnsDesktop(getMultiColGeoUnits(foundOptions));
      }
      setOptions(foundOptions);
    } catch (err) {
      logger.log(err);
    }
    setIsLoading(false);
    return foundOptions;
  };

  const handleToggle = async (): Promise<void> => {
    if (options.length === 0) {
      await loadOptions();
    }
    if (isOpen) {
      setSelectedOptions([...value]);
    }
    setIsOpen(!isOpen);
  };

  useClickAway(popupRef, (event: Event): void => {
    if (!outerRef.current?.contains(event.target as Node)) {
      if (triggerSearchOnLeave) {
        apply();
      } else {
        setIsOpen(false);
      }
    }
  });

  const toggleOpen = async (): Promise<void> => {
    if (changeOnBlur && isOpen) {
      onChange([...selectedOptions]);
    }
    await handleToggle();
  };

  const apply = (newOptions?: ApiGeoUnit[]): void => {
    onChange(newOptions ? [...newOptions] : [...selectedOptions]);
    setIsOpen(false);
  };

  const onOptionClick = (option: ApiGeoUnit): void => {
    const foundOption = selectedOptions.find(selectedOption => selectedOption?.id === option.id);
    let newSelectedOptions = [...selectedOptions];
    if (foundOption) {
      newSelectedOptions = [
        ...newSelectedOptions.filter(selectedOption => selectedOption?.id !== option.id)
      ];
    } else {
      newSelectedOptions = [...newSelectedOptions, { ...option }];
    }
    setSelectedOptions(newSelectedOptions);
    if (!withApplyButton) {
      onChange([...newSelectedOptions]);
    }
  };

  const handleClick = async (
    e: MouseEvent<HTMLInputElement | HTMLSpanElement> | Event
  ): Promise<void> => {
    e.stopPropagation();
    if (!disabled) {
      await toggleOpen();
    }
  };

  const handleKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement | HTMLSpanElement | HTMLElement>
  ): Promise<void> => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      await toggleOpen();
    }

    if (e.key === 'Tab') {
      setTimeout(() => {
        if (
          popupRef.current &&
          outerRef.current &&
          !popupRef.current.contains(document.activeElement) &&
          !outerRef.current.contains(document.activeElement)
        ) {
          setIsOpen(false);
        }
      }, 0);
    }
  };

  const onIconClick = async (e: MouseEvent<HTMLInputElement | HTMLSpanElement> | Event) => {
    e.stopPropagation();
    if (value.length > 0) {
      setSelectedOptions([]);
      if (!withApplyButton) {
        onChange([]);
      }
    }
    await handleToggle();
    if (options.length === 0) {
      await loadOptions();
    }
    setIsOpen(!isOpen);
  };

  const fieldValue = value.map(option => option?.name || '').join(', ');
  const { onPseudoGeoOptionClick, allOptionsSelected } = usePseudoGeoOption({
    options,
    onOptionClick,
    selectedOptions,
    setSelectedOptions
  });

  const renderCheckbox = (option: ApiGeoUnit) => {
    const isPseudo = option.typeCode === TypeCodeKey.PSEUDO;
    if (isPseudo && showPseudo) {
      return (
        <GeoUnitPseudo
          onOptionClick={onPseudoGeoOptionClick}
          option={option}
          key={option.id}
          selectedOptions={selectedOptions}
          allOptionsSelected={allOptionsSelected}
        />
      );
    }
    return (
      <CheckBox
        key={option.id}
        id={option.id}
        name={option.name}
        onChange={(): void => onOptionClick(option)}
        checked={!!selectedOptions.find(selectedOption => selectedOption?.id === option.id)}
      />
    );
  };

  return (
    <div
      ref={outerRef}
      className={`geounit-checkbox${isOpen && options.length > 0 ? ' geounit-checkbox--open' : ''}${
        disabled ? ' geounit-checkbox--disabled' : ''
      }`}
      onKeyDown={handleKeyDown}
    >
      <div>
        <span
          className="geounit-checkbox__angle__wrapper"
          onClick={withDismissAllIcon ? onIconClick : handleClick}
          onKeyDown={handleKeyDown}
        >
          {withDismissAllIcon && value.length ? (
            <FontAwesomeIcon className="geounit-checkbox__angle" icon={faTimesCircle} />
          ) : (
            <FontAwesomeIcon className="geounit-checkbox__angle" icon={faAngleDown} />
          )}
        </span>
        <Input
          className={`${fieldValue ? 'input--filled' : ''} input--blue-outline`}
          innerRef={inputRef}
          name={name ?? ''}
          placeholder={placeholder ?? ''}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onFocus={e => e.target.setSelectionRange(0, 0)}
          value={staticLabel ?? valueToShow ?? fieldValue}
          readOnly
          disabled={disabled}
          aria-label={staticLabel}
        />
      </div>
      {isLoading && (
        <div className="geounit-checkbox__loading">
          <Spinner />
        </div>
      )}
      {isOpen && options.length > 0 ? (
        <div className="geounit-checkbox__popover" ref={popupRef}>
          <div>
            <div
              className={`geounit-checkbox__container ${
                options.length > MAX_NUMBER_PER_COLUMN && changeNumberOfColumnDynamically
                  ? 'geounit-checkbox__container--multiple-columns'
                  : ''
              }`}
            >
              <button
                type="button"
                className="geounit-checkbox__close btn-reset"
                aria-label="Close"
                onClick={e => {
                  e.stopPropagation();
                  apply();
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    apply();
                  }
                }}
              >
                <FontAwesomeIcon className="geounit-checkbox__angle" icon={faTimesCircle} />
              </button>
              <div
                className={`geounit-checkbox__container__inner ${
                  options.length > MAX_NUMBER_PER_COLUMN && changeNumberOfColumnDynamically
                    ? 'geounit-checkbox__container__inner--multiple-columns'
                    : ''
                }`}
              >
                {options && withSelectAll && (
                  <CheckBox
                    id={'select-all'}
                    name={
                      selectedOptions.length === options.length
                        ? t(Keys.removeAll)
                        : t(Keys.selectAll)
                    }
                    onChange={(): void => {
                      const toSelect = selectedOptions.length === options.length ? [] : options;
                      setSelectedOptions(toSelect);
                      if (!withApplyButton) {
                        onChange([...toSelect]);
                      }
                    }}
                    checked={selectedOptions.length === options.length}
                  />
                )}

                <div className="geounit-checkbox__options">{options.map(renderCheckbox)}</div>
                {useTwoColumnStyling && (
                  <div className="geounit-checkbox__options--desktop">
                    {optionsForTwoColumnsDesktop.map(renderCheckbox)}
                  </div>
                )}
              </div>
              {withRemoveAll || withApplyButton ? (
                <div className="geounit-checkbox__button-container d-flex justify-content-center mt-4 mb-2">
                  {withRemoveAll && selectedOptions.length ? (
                    <button
                      type="button"
                      className="btn geounit-checkbox__btn-remove"
                      onClick={() => setSelectedOptions([])}
                    >
                      {t(Keys.removeAllChoices)}
                    </button>
                  ) : null}
                  {withApplyButton ? (
                    <Button color="secondary" onClick={(): void => apply()}>
                      {t(Keys.apply)}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const GeoUnitPseudo = ({
  option,
  onOptionClick,
  selectedOptions,
  allOptionsSelected
}: {
  option: ApiGeoUnit;
  onOptionClick: (a: ApiGeoUnit) => void;
  selectedOptions: ApiGeoUnit[];
  allOptionsSelected: boolean;
}) => {
  const { t } = useTranslation('geo-unit-checkbox-async', fallback, Keys);
  const geoUnitChildren = Object.keys(option.children).map(key => option.children[key]);
  const flattedGeoUnitChildren = geoUnitChildren.flatMap(itm => itm.code);
  const isPseudoChecked =
    option.code === PSEUDO_CODE.ALL
      ? allOptionsSelected
      : selectedOptions.filter(selectedOption =>
          flattedGeoUnitChildren.includes(selectedOption.code)
        ).length === flattedGeoUnitChildren.length;

  return (
    <div className="geounit-checkbox__container__group">
      <CheckBox
        id={option.id}
        onChange={() => onOptionClick(option)}
        name={t((Keys as Record<string, string>)[option.code])}
        checked={isPseudoChecked}
      />
      {geoUnitChildren.map(child => (
        <CheckBox
          id={child.id}
          key={child.id}
          onChange={() => onOptionClick(child)}
          name={child.name}
          checked={!!selectedOptions.find(selectedOption => selectedOption?.id === child.id)}
        />
      ))}
    </div>
  );
};

const CheckBox = ({
  id,
  checked,
  onChange,
  name
}: {
  id: string;
  checked?: boolean;
  onChange: () => void;
  name: string;
}) => {
  return (
    <div className="geounit-checkbox__field">
      <CustomInput
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        label={<span>{name}</span>}
      />
    </div>
  );
};

export default GeoUnitCheckboxAsync;
